import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import XLSX from "xlsx-js-style";
import ProjectEstimatesService from "../services/ProjectEstimatesService";
import ProjectService from "../services/ProjectService";
import ProjectStoreService from "../services/ProjectStoreService";
import ProjectLocationService from "../services/ProjectLocationService";
import ProjectItemService from "../services/ProjectItemService";
import Breadcrumb from "../components/Breadcrumb";
import StorageService from "../services/StorageService";
import ReactPaginate from "react-paginate";
import Loading from "../components/loading";
import ScreenData from "../config/ScreenData";
import usePermission from "../utils/UsePermission";
import UnAuthorized from "../views/Unathorized";
import MessageDialog from "../components/MessageDialog";
import utility from "../utils/utility";

export default function ViewProjectEstimates() {
  const [estimates, setEstimates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Dropdown lists
  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);
  const [locations, setLocations] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);

  // Selected filters
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedItemCategoryId, setSelectedItemCategoryId] = useState("");

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const { permission, can } = usePermission(
    ScreenData.Project_Estimates,
    showMessage
  );

  // Load projects for dropdown on mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const companyId = StorageService.activeCompany()?.companyId;
        const employeeId = StorageService.loginData()?.employeeId;
        const roleId = Number(StorageService.loginData()?.roleId);

        let res;
        if (roleId === 1 || roleId === 2) {
          res = await ProjectService.getActive(companyId);
        } else {
          res = await ProjectService.getActiveProjectsByEmployee(companyId, employeeId);
        }
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setProjects(list);
      } catch (err) {
        console.error("Error loading projects for dropdown:", err);
      }
    };

    loadProjects();
  }, []);

  // Fetch estimates when filters or page change
  useEffect(() => {
    if (permission === null) return;
    if (permission.canView !== 1) return;
    fetchEstimates();
  }, [currentPage, selectedProjectId, selectedStoreId, selectedLocationId, selectedItemCategoryId, permission]);

  const fetchEstimates = async () => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany()?.companyId;
      const employeeId = StorageService.loginData()?.employeeId || "";
      const roleId = StorageService.loginData()?.roleId || "";
      const response = await ProjectEstimatesService.getAllProjectEstimates(
        companyId,
        currentPage,
        itemsPerPage,
        "",
        employeeId,
        roleId,
        selectedProjectId,
        selectedStoreId,
        selectedLocationId,
        selectedItemCategoryId
      );
      setEstimates(response.data || []);
      setPageCount(response.totalPages || 0);
    } catch (err) {
      console.error(err);
      showMessage("Error fetching project estimates.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reset page to 1 and deselect row when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelected(null);
  }, [selectedProjectId, selectedStoreId, selectedLocationId, selectedItemCategoryId]);

  // ----------------------------------------------------
  // Cascading Handlers: Project -> Store -> Location -> Item Category
  // ----------------------------------------------------
  const handleProjectChange = async (opt) => {
    const projId = opt ? opt.value : "";
    setSelectedProjectId(projId);
    setSelectedStoreId("");
    setSelectedLocationId("");
    setSelectedItemCategoryId("");
    setStores([]);
    setLocations([]);
    setItemCategories([]);

    if (projId) {
      try {
        const [storesRes, itemsRes] = await Promise.all([
          ProjectStoreService.getProjectStoreById(projId),
          ProjectItemService.getByProjectId(projId)
        ]);
        const storeList = Array.isArray(storesRes?.data) ? storesRes.data : Array.isArray(storesRes) ? storesRes : [];
        setStores(storeList);

        const itemsList = Array.isArray(itemsRes?.data?.data) ? itemsRes.data.data : Array.isArray(itemsRes?.data) ? itemsRes.data : Array.isArray(itemsRes) ? itemsRes : [];
        const uniqueCategories = [];
        const seen = new Set();
        itemsList.forEach((item) => {
          if (item.itemCategoryId && !seen.has(item.itemCategoryId)) {
            seen.add(item.itemCategoryId);
            uniqueCategories.push({
              id: item.itemCategoryId,
              itemCategoryName: item.itemCategoryName,
              itemCategoryCode: item.itemCategoryCode || ""
            });
          }
        });
        setItemCategories(uniqueCategories);
      } catch (err) {
        console.error("Error fetching stores/items for project:", err);
        setStores([]);
        setItemCategories([]);
      }
    }
  };

  const handleStoreChange = async (opt) => {
    const storeId = opt ? opt.value : "";
    setSelectedStoreId(storeId);
    setSelectedLocationId("");
    setLocations([]);

    if (storeId && selectedProjectId) {
      try {
        const res = await ProjectLocationService.getByProjectAndStore(selectedProjectId, storeId);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setLocations(list);
      } catch (err) {
        console.error("Error fetching locations:", err);
        setLocations([]);
      }
    }
  };

  const handleLocationChange = (opt) => {
    const locId = opt ? opt.value : "";
    setSelectedLocationId(locId);
  };

  const handleItemCategoryChange = (opt) => {
    const catId = opt ? opt.value : "";
    setSelectedItemCategoryId(catId);
  };

  const handleClear = () => {
    setSelectedProjectId("");
    setSelectedStoreId("");
    setSelectedLocationId("");
    setSelectedItemCategoryId("");
    setStores([]);
    setLocations([]);
    setItemCategories([]);
    setCurrentPage(1);
    setSelected(null);
  };

  // ----------------------------------------------------
  // Export Excel (Styled referencing Stock In report)
  // ----------------------------------------------------
  const exportExcel = async () => {
    if (!can("canView")) {
      showMessage("No permission to export", "error");
      return;
    }

    try {
      setLoading(true);
      const company = StorageService.activeCompany();
      const login = StorageService.loginData();
      const employeeId = login?.employeeId || "";
      const roleId = login?.roleId || "";

      const res = await ProjectEstimatesService.getAllProjectEstimates(
        company?.companyId,
        1,
        100000,
        "",
        employeeId,
        roleId,
        selectedProjectId,
        selectedStoreId,
        selectedLocationId,
        selectedItemCategoryId
      );

      const data = res?.data || [];
      if (!data.length) {
        showMessage("No data found to export", "error");
        return;
      }

      // Filter Labels
      let projectLabel = "All";
      if (selectedProjectId) {
        const found = projects.find((p) => String(p.id || p.projectId) === String(selectedProjectId));
        projectLabel = found ? `${found.projectName}${found.projectCode ? ` - ${found.projectCode}` : ""}` : selectedProjectId;
      }

      let storeLabel = "All";
      if (selectedStoreId) {
        const found = stores.find((s) => String(s.storeId || s.id) === String(selectedStoreId));
        storeLabel = found ? `${found.storeName}${found.storeCode ? ` - ${found.storeCode}` : ""}` : selectedStoreId;
      }

      let locationLabel = "All";
      if (selectedLocationId) {
        const found = locations.find((l) => String(l.locationId || l.id) === String(selectedLocationId));
        locationLabel = found?.locationName || selectedLocationId;
      }

      let itemCategoryLabel = "All";
      if (selectedItemCategoryId) {
        const found = itemCategories.find((ic) => String(ic.id) === String(selectedItemCategoryId));
        itemCategoryLabel = found ? `${found.itemCategoryName}${found.itemCategoryCode ? ` - ${found.itemCategoryCode}` : ""}` : selectedItemCategoryId;
      }

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      let row = 0;
      const companyName = company?.companyName || "Company";

      const thin = { style: "thin" };
      const center = {
        horizontal: "center",
        vertical: "center",
        wrapText: true
      };

      // 1. COMPANY NAME (Top Left) & REPORT DATE (Top Right)
      const headerReportDateStr = `Report Date: ${utility.formatExportDateTime(new Date())}`;
      XLSX.utils.sheet_add_aoa(
        ws,
        [[companyName, "", "", "", "", "", headerReportDateStr, "", "", "", ""]],
        { origin: { r: row, c: 0 } }
      );
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 5 } });
      ws["!merges"].push({ s: { r: row, c: 6 }, e: { r: row, c: 10 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
        font: { bold: true, color: { rgb: "000000" }, sz: 12 },
        alignment: { horizontal: "left", vertical: "center" }
      };
      ws[XLSX.utils.encode_cell({ r: row, c: 6 })].s = {
        font: { bold: true, color: { rgb: "000000" }, sz: 10 },
        alignment: { horizontal: "right", vertical: "center" }
      };

      row++;

      // 2. TITLE
      XLSX.utils.sheet_add_aoa(ws, [["Project Estimates Report"]], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 10 } });
      const titleCell = XLSX.utils.encode_cell({ r: row, c: 0 });
      ws[titleCell].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center", vertical: "center" }
      };

      row += 2;

      // 3. FILTER INFO
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          ["Project:", projectLabel, "", "Store:", storeLabel, "", "Location:", locationLabel, "", "Item Category:", itemCategoryLabel]
        ],
        { origin: { r: row, c: 0 } }
      );

      const filterLabelStyle = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: "left", vertical: "center" },
        border: { top: thin, bottom: thin, left: thin, right: thin }
      };

      const filterValueStyle = {
        font: { sz: 10 },
        alignment: { horizontal: "left", vertical: "center" },
        border: { top: thin, bottom: thin, left: thin, right: thin }
      };

      const boldCols = [0, 3, 6, 9];
      for (let c = 0; c < 11; c++) {
        const cell = XLSX.utils.encode_cell({ r: row, c });
        if (!ws[cell]) continue;
        if (boldCols.includes(c)) {
          ws[cell].s = filterLabelStyle;
        } else {
          ws[cell].s = filterValueStyle;
        }
      }
      row += 2;

      // 4. TABLE HEADER
      const headers = [
        "Sl No",
        "Project",
        "Store",
        "Location",
        "Item Category",
        "Item Code",
        "Item Description",
        "UOM",
        "Estimated Qty",
        "Dashboard",
        "Stockout Check",
        "Status"
      ];

      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });

      headers.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        ws[cell].s = {
          font: { bold: true, sz: 11 },
          alignment: center,
          fill: { fgColor: { rgb: "E6E6E6" } },
          border: {
            top: thin,
            bottom: thin,
            left: thin,
            right: thin
          }
        };
      });
      row++;

      // 5. TABLE DATA
      data.forEach((item, index) => {
        const rowData = [
          index + 1,
          item.projectName ? `${item.projectName}${item.projectCode ? ` - ${item.projectCode}` : ""}` : "-",
          item.storeName ? `${item.storeName}${item.storeCode ? ` - ${item.storeCode}` : ""}` : "-",
          item.locationName || "-",
          item.itemCategoryName || "-",
          item.itemCode || "-",
          item.itemDescription || "-",
          item.UOM || "-",
          Number(item.estimatedQuantity) || 0,
          item.displayOnDashboard === 1 ? "Yes" : "No",
          item.stockoutCheck === 1 ? "Yes" : "No",
          item.status || "-"
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });

        rowData.forEach((_, colIndex) => {
          const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
          ws[cell].s = {
            font: { sz: 10 },
            alignment: {
              horizontal:
                colIndex === 0 || colIndex === 8
                  ? "right"
                  : colIndex === 9 || colIndex === 10 || colIndex === 11
                  ? "center"
                  : "left",
              vertical: "center"
            },
            border: {
              top: thin,
              bottom: thin,
              left: thin,
              right: thin
            }
          };
        });
        row++;
      });

      // 6. COLUMN WIDTHS
      ws["!cols"] = [
        { wch: 8 },  // Sl No
        { wch: 25 }, // Project
        { wch: 20 }, // Store
        { wch: 20 }, // Location
        { wch: 30 }, // Item Category
        { wch: 40 }, // Item Code
        { wch: 30 }, // Item Description
        { wch: 10 }, // UOM
        { wch: 15 }, // Estimated Qty
        { wch: 12 }, // Dashboard
        { wch: 15 }, // Stockout Check
        { wch: 12 }  // Status
      ];

      XLSX.utils.book_append_sheet(wb, ws, "ProjectEstimates");
      const fileName = `Project_Estimates_${utility.formatDateDMY(new Date()).replace(/\//g, "-")}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      showMessage("Failed to export Excel", "error");
    } finally {
      setLoading(false);
    }
  };

  // react-select options
  const projectOptions = projects.map((p) => {
    const id = p.id || p.projectId;
    const code = p.projectCode ? ` - ${p.projectCode}` : "";
    return { value: String(id), label: `${p.projectName}${code}` };
  });

  const storeOptions = stores.map((s) => {
    const id = s.storeId || s.id;
    const code = s.storeCode ? ` - ${s.storeCode}` : "";
    return { value: String(id), label: `${s.storeName}${code}` };
  });

  const locationOptions = locations.map((l) => {
    const id = l.locationId || l.id;
    return { value: String(id), label: l.locationName };
  });

  const itemCategoryOptions = itemCategories.map((ic) => {
    const id = ic.id;
    const code = ic.itemCategoryCode ? ` - ${ic.itemCategoryCode}` : "";
    return { value: String(id), label: `${ic.itemCategoryName}${code}` };
  });

  const handleSelect = (id) => {
    setSelected(id);
  };

  const handleAdd = () => {
    navigate("/cpnl/projectestimates/add");
  };

  const handleModify = () => {
    if (!selected) {
      showMessage("Please select one Estimate to modify.");
      return;
    }

    const est = estimates.find((e) => e.id === selected);

    if (est?.status === "Inactive") {
      showMessage("Inactive records cannot be modified", "error");
      return;
    }

    navigate(`/cpnl/projectestimates/add/${selected}`);
  };

  const handleDelete = async () => {
    if (!selected) {
      showMessage("Please select one Estimate to delete.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this estimate?")) return;

    try {
      setLoading(true);
      await ProjectEstimatesService.deleteProjectEstimate(selected);
      setSelected(null);
      fetchEstimates();
      showMessage("Estimate deleted successfully.", "success");
    } catch (err) {
      console.error(err);
      showMessage("Failed to delete estimate.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (est) => {
    if (!can("canModify")) {
      showMessage("You don't have permission to modify status", "error");
      return;
    }
    const newStatus = est.status === "Active" ? "Inactive" : "Active";

    if (!window.confirm(`Update status to ${newStatus}?`)) return;

    try {
      await ProjectEstimatesService.updateStatus(est.id);
      fetchEstimates();
      showMessage("Status updated successfully.", "success");
    } catch (error) {
      console.error(error);
      showMessage("Error updating status.", "error");
    }
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };

  if (permission === null) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!permission || permission.canView !== 1) {
    return <UnAuthorized />;
  }

  return (
    <>
      <Breadcrumb pageTitle="Project Estimates" secondTitle="Projects" />

      <div className="row">
        <div className="card py-3 px-4 bg-white shadow-sm">
          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          {/* FILTERS ROW (TOP) */}
          <div className="d-flex align-items-center gap-3 flex-wrap mb-3 pb-2 border-bottom">
            {/* 1. PROJECT */}
            <div className="d-flex flex-column" style={{ minWidth: "220px", flex: "1 1 200px" }}>
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>Project</label>
              <Select
                options={projectOptions}
                value={projectOptions.find((opt) => opt.value === String(selectedProjectId)) || null}
                onChange={handleProjectChange}
                isSearchable={true}
                isClearable={true}
                placeholder="Select Project"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: "38px" })
                }}
              />
            </div>

            {/* 2. STORE */}
            <div className="d-flex flex-column" style={{ minWidth: "220px", flex: "1 1 200px" }}>
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>Store</label>
              <Select
                options={storeOptions}
                value={storeOptions.find((opt) => opt.value === String(selectedStoreId)) || null}
                onChange={handleStoreChange}
                isDisabled={!selectedProjectId}
                isSearchable={true}
                isClearable={true}
                placeholder={!selectedProjectId ? "Select Project first" : "Select Store"}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: "38px" })
                }}
              />
            </div>

            {/* 3. LOCATION */}
            <div className="d-flex flex-column" style={{ minWidth: "220px", flex: "1 1 200px" }}>
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>Location</label>
              <Select
                options={locationOptions}
                value={locationOptions.find((opt) => opt.value === String(selectedLocationId)) || null}
                onChange={handleLocationChange}
                isDisabled={!selectedStoreId}
                isSearchable={true}
                isClearable={true}
                placeholder={!selectedStoreId ? "Select Store first" : "Select Location"}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: "38px" })
                }}
              />
            </div>

            {/* 4. ITEM CATEGORY */}
            <div className="d-flex flex-column" style={{ minWidth: "220px", flex: "1 1 200px" }}>
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>Item Category</label>
              <Select
                options={itemCategoryOptions}
                value={itemCategoryOptions.find((opt) => opt.value === String(selectedItemCategoryId)) || null}
                onChange={handleItemCategoryChange}
                isDisabled={!selectedProjectId}
                isSearchable={true}
                isClearable={true}
                placeholder={!selectedProjectId ? "Select Project first" : "Select Item Category"}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: "38px" })
                }}
              />
            </div>

            {/* CLEAR / RESET BUTTON */}
            <div className="d-flex flex-column align-self-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClear}
                style={{ height: "38px", minWidth: "80px" }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* ACTIONS ROW */}
          <div className="mb-3 pb-2 border-bottom d-flex justify-content-end gap-2 flex-wrap">
            <button
              className="btn btn-success"
              onClick={exportExcel}
              disabled={loading}
            >
              <i className="ti ti-file-spreadsheet me-1"></i> Export Excel
            </button>

            {can("canAdd") && (
              <button className="btn btn-success" onClick={handleAdd}>
                <i className="ti ti-plus me-1"></i> Add
              </button>
            )}

            {can("canModify") && (
              <button
                className="btn btn-warning"
                onClick={handleModify}
                disabled={!selected}
              >
                <i className="ti ti-pencil me-1"></i> Modify
              </button>
            )}

            {can("canDelete") && (
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={!selected}
              >
                <i className="ti ti-trash me-1"></i> Delete
              </button>
            )}
          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>Select</th>
                  <th>Project</th>
                  <th>Store</th>
                  <th>Location</th>
                  <th>Item Category</th>
                  <th>Item</th>
                  <th>UOM</th>
                  <th>Estimated Qty</th>
                  <th>Dashboard</th>
                  <th>Stockout Check</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      <Loading />
                    </td>
                  </tr>
                ) : estimates.length > 0 ? (
                  estimates.map((est) => {
                    const isActive = est.status === "Active";
                    return (
                      <tr key={est.id}>
                        <td>
                          <input
                            type="radio"
                            name="estimateSelect"
                            disabled={!isActive}
                            checked={selected === est.id}
                            onChange={() => handleSelect(est.id)}
                          />
                        </td>
                        <td>{est.projectName ? `${est.projectName} - ${est.projectCode}` : "-"}</td>
                        <td>{est.storeName ? `${est.storeName} - ${est.storeCode}` : "-"}</td>
                        <td>{est.locationName || "-"}</td>
                        <td>{est.itemCategoryName || "-"}</td>
                        <td>{est.itemCode ? `${est.itemCode} - ${est.itemDescription}` : "-"}</td>
                        <td>{est.UOM || "-"}</td>
                        <td>{est.estimatedQuantity}</td>
                        <td>{est.displayOnDashboard === 1 ? "Yes" : "No"}</td>
                        <td>{est.stockoutCheck === 1 ? "Yes" : "No"}</td>
                        <td>
                          <span
                            className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}
                            style={{ cursor: can("canModify") ? "pointer" : "default" }}
                            onClick={() => handleToggleStatus(est)}
                          >
                            {est.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      No project estimates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              previousLabel="< Previous"
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
            />
          </div>
        </div>
      </div>
    </>
  );
}
